import React, { useEffect, useCallback } from "react";

import FullPageLoader from "../FullPageLoader";

import * as authApi from "js/api/authApi";
import { LoginDelegatedRequest, LoginResponse } from "js/api/authApi";
import { useUserSessionStore } from "stores/v2/userSessionStore";
import { useLocation, useNavigate } from "react-router-dom";
import { LoginProvider } from "js/api/loginApi";
import { datadogRum } from "@datadog/browser-rum";
const LoginClasslinkCallback: React.FC = () => {
	const location = useLocation();
	const navigate = useNavigate();
	const handleLoginFailure = useCallback(
		(error: unknown): void => {
			const apiError = error as authApi.ApiError<authApi.LoginMethodError> | authApi.ApiError<authApi.ServerError>;
			useUserSessionStore.getState().handleAuthenticationFailure(apiError);

			// Extract applianceId from state query parameter if it exists
			// "state" value will only exist if initiated from School specific login page
			const searchParams = new URLSearchParams(location.search);
			const applianceId = searchParams.get("state") || undefined;

			// Track as user-facing error in RUM
			// Extract error message from respJson if available (ServerError case)
			// apiError.error is always undefined because FetchStatusError doesn't have an error property
			const errorMessage =
				apiError.respJson && "error" in apiError.respJson
					? apiError.respJson.error
					: error instanceof Error
						? error.message
						: "login failed";

			datadogRum.addError({
				message: "ClassLink OAuth login failed",
				source: "custom",
				stack: error instanceof Error ? error.stack : undefined,
				context: {
					provider: "ClassLink",
					errorType: errorMessage,
					errorMessage: errorMessage,
					...(applianceId ? { applianceId } : {}),
					...(apiError.respJson && "provider" in apiError.respJson ? { providerFromError: apiError.respJson.provider } : {}),
				},
			});

			if (apiError.respJson && "provider" in apiError.respJson) {
				navigate("/login", { state: { provider: apiError.respJson.provider } });
			} else {
				navigate("/login", { state: { error: "Classlink" } });
			}
		},
		[navigate, location]
	);

	useEffect(() => {
		const searchParams = new URLSearchParams(location.search);
		const loginRequest: LoginDelegatedRequest = {
			code: searchParams.get("code") || "",
			deviceid: searchParams.get("state") || "",
		};

		authApi
			.loginDelegated(LoginProvider.CLASSLINK, loginRequest)
			.then((res: LoginResponse) => {
				useUserSessionStore
					.getState()
					.handleAuthenticationSuccess(res)
					.then(() => navigate("/"))
					.catch(handleLoginFailure);
			})
			.catch(handleLoginFailure);
	}, [navigate, handleLoginFailure, location]);

	return <FullPageLoader message="Logging in with Classlink" />;
};

export default LoginClasslinkCallback;
